import React from 'react';
import { observer } from 'mobx-react-lite';
import type { ArrowElement, Point } from '@/types';

import { getStrokeDashArray, getStrokeLineCap } from '@/utils/strokeStyles';
import { generateSmoothPath, calculateMiddleControlPoint } from '@/utils/bezier';

interface ArrowProps {
  element: ArrowElement;
  isSelected: boolean;
}

export const Arrow: React.FC<ArrowProps> = observer(({ element, isSelected }) => {
  const { style, arrowHeadSize } = element;
  
  // Support both new points array and legacy startPoint/endPoint
  let points: Point[] = [];
  if (element.points && element.points.length >= 2) {
    points = element.points;
    
    // If we only have 2 points and they're the same, don't render
    if (points.length === 2 && points[0] && points[1] &&
        points[0].x === points[1].x && 
        points[0].y === points[1].y) {
      return null;
    }
  } else if (element.startPoint && element.endPoint) {
    // Legacy support - convert to 3-point bezier with middle control point
    const middleControl = calculateMiddleControlPoint(
      element.startPoint, 
      element.endPoint, 
      20 // slight curve offset
    );
    points = [element.startPoint, middleControl, element.endPoint];
  } else {
    // Invalid arrow data - don't render anything
    console.warn('Arrow element has invalid point data:', element);
    return null;
  }

  const startPoint = points[0];
  const endPoint = points[points.length - 1];
  if (!startPoint || !endPoint) return null;
  
  // Generate smooth bezier path for the line
  const smoothPath = generateSmoothPath(points);
  
  // Generate arrowhead at the end point
  // Use the last two points to determine the direction for a more accurate arrowhead
  const penultimatePoint = points.length > 1 ? points[points.length - 2] : startPoint;
  const angle = Math.atan2(
    endPoint.y - (penultimatePoint?.y || startPoint.y), 
    endPoint.x - (penultimatePoint?.x || startPoint.x)
  );
  const headSize = arrowHeadSize || 12;
  const arrowHead1X = endPoint.x - headSize * Math.cos(angle - Math.PI / 6);
  const arrowHead1Y = endPoint.y - headSize * Math.sin(angle - Math.PI / 6);
  const arrowHead2X = endPoint.x - headSize * Math.cos(angle + Math.PI / 6);
  const arrowHead2Y = endPoint.y - headSize * Math.sin(angle + Math.PI / 6);
  
  const arrowheadPath = `M ${endPoint.x} ${endPoint.y} L ${arrowHead1X} ${arrowHead1Y} M ${endPoint.x} ${endPoint.y} L ${arrowHead2X} ${arrowHead2Y}`;
  

  
  return (
    <g>
      {/* Arrow line */}
      <path
        d={smoothPath}
        fill="none"
        stroke={style.strokeColor}
        strokeWidth={style.strokeWidth}
        strokeDasharray={getStrokeDashArray(style.strokeStyle, style.strokeWidth)}
        opacity={style.opacity}
        strokeLinecap={getStrokeLineCap(style.strokeStyle)}
        strokeLinejoin="round"
      />
      
      {/* Arrow head */}
      <path
        d={arrowheadPath}
        fill="none"
        stroke={style.strokeColor}
        strokeWidth={style.strokeWidth}
        strokeDasharray={getStrokeDashArray(style.strokeStyle, style.strokeWidth)}
        opacity={style.opacity}
        strokeLinecap={getStrokeLineCap(style.strokeStyle)}
        strokeLinejoin="round"
      />
      
      {isSelected && (
        <g>
          {/* Selection highlight */}
          <path
            d={smoothPath}
            fill="none"
            stroke="#007acc"
            strokeWidth={style.strokeWidth + 4}
            opacity={0.3}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          {/* Control point handles */}
          {points.map((point, index) => (
            <circle
              key={`control-${index}`}
              cx={point.x}
              cy={point.y}
              r={4}
              fill={index === 0 || index === points.length - 1 ? "#007acc" : "#ff6b35"}
              stroke="#ffffff"
              strokeWidth={1}
              style={{ cursor: 'move' }}
            />
          ))}
        </g>
      )}
    </g>
  );
}); 